import React, {
  memo,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { up } from 'styled-breakpoints';
import styled, { css, keyframes } from 'styled-components';
import { match, P } from 'ts-pattern';
import { ColorPaletteItem } from '@lgg/isomorphic';
import {
  ContactInteractionUnion,
  ContactInteractionDirection,
  LeadHistoryLogStatus,
  ContactInteractionComponent,
  RegistrationType,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { ManualRegistrationIcon } from 'src/components/domain/contact-interaction/manual-registration-icon';
import { useOutstandingItem } from 'src/components/general/display/inverted-virtuoso';
import { Tooltip } from 'src/components/general/display/tooltip';
import { Icon } from 'src/components/general/icon';
import { Center } from 'src/components/layout/center';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  VideoOverlay,
  PlayIcon,
} from 'src/components/pages/conversations/components/contact-interactions/items/attachment-items/shared';
import {
  ContactInteractionDetails,
  ContactInteractionDetailsCaretVariant,
  ContactInteractionDetailsPopoverData,
} from 'src/components/pages/conversations/components/contact-interactions/items/contact-interaction-details';
import { InteractionTime } from 'src/components/pages/conversations/components/contact-interactions/items/interaction-time';
import { RepliedItemPreview } from 'src/components/pages/conversations/components/contact-interactions/items/replied-item-preview';
import { AttachmentPreviewModal } from 'src/components/pages/conversations/components/general/attachment-preview-modal';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';
import { fadeInAnimation } from 'src/theme/animations';

export enum MessageBubbleWithDirectionContainerVariant {
  REGULAR = 'regular',
  REFERRAL = 'referral',
  STORY_REPLY = 'story-reply',
}

export const messageBubbleMaxWidthStyle = css`
  ${({ $variant }: { $variant?: MessageBubbleWithDirectionContainerVariant }) => {
    return (
      match($variant)
        .with(
          MessageBubbleWithDirectionContainerVariant.REFERRAL,
          () => css`
            max-width: min(80%, 310px);
          `,
        )
        .with(
          MessageBubbleWithDirectionContainerVariant.STORY_REPLY,
          () => css`
            max-width: min(80%, 226px);

            ${up('md')} {
              max-width: min(80%, 246px);
            }
          `,
        )
        // fallback to regular
        .otherwise(
          () => css`
            max-width: min(80%, 565px);
          `,
        )
    );
  }};
`;

export const messageBubblesStyles = css`
  ${messageBubbleMaxWidthStyle};
  box-shadow: 0 2px 4px 0 #98a9bc0f;
  display: flex;
  flex-direction: column;
  word-break: break-word;
  position: relative;
`;

export const BubbleDivider = styled.div<{ direction: 'INBOUND' | 'OUTBOUND' }>`
  background-color: ${({ theme, direction }) =>
    direction === 'INBOUND' ? theme.colors.koala : theme.colors.flint};
  height: 1px;
  margin: 15px -15px;
  opacity: ${({ direction }) => (direction === 'INBOUND' ? 1 : 0.2)};

  ${up('md')} {
    background-color: ${({ theme, direction }) =>
      direction === 'INBOUND' ? theme.colors.porcelain : theme.colors.flint};
  }
`;

export const StyledInteractionTextContent = styled.p<{
  direction: 'INBOUND' | 'OUTBOUND';
}>`
  color: ${({ theme, direction }) =>
    direction === 'INBOUND' ? theme.colors.storm : theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  letter-spacing: -0.18px;
  line-height: 15px;
  margin: 0 0 5px;

  ${up('md')} {
    font-size: 14px;
    letter-spacing: -0.2px;
    line-height: 16px;
  }
`;

export const InteractionTextContent = memo<{
  direction: 'INBOUND' | 'OUTBOUND';
  children: ReactNode;
}>((props) => {
  return (
    <TruncatedText>
      <StyledInteractionTextContent {...props} />
    </TruncatedText>
  );
});

export const ciHighlightedAnimation = keyframes`
  0% {
    outline: 2px solid transparent;
    outline-offset: -3px;
  }

  33% {
    outline: 2px solid rgba(45, 152, 218, 0.5);
    outline-offset: 1px;

  }

  66% {
    outline-offset: 2px;
  }

  100% {
    outline: 2px solid transparent;
    outline-offset: 4px;
  }
`;

const noteHighlightedAnimation = keyframes`
  0% {
    outline: 2px solid transparent;
    outline-offset: -3px;
  }

  33% {
    outline: 2px solid rgba(255, 185, 0, 0.75);
    outline-offset: 1px;

  }

  66% {
    outline-offset: 2px;
  }

  100% {
    outline: 2px solid transparent;
    outline-offset: 4px;
  }
`;

export const MessageBubbleWithDirectionContainer = styled.div<{
  direction: ContactInteractionDirection;
  registrationType: RegistrationType;
  backgroundColor?: ColorPaletteItem;
  forceMessageCaret?: Boolean;
  $variant?: MessageBubbleWithDirectionContainerVariant;
}>`
  ${messageBubblesStyles};
  background-color: ${({ direction, backgroundColor, theme }) =>
    backgroundColor
      ? theme.colors[backgroundColor]
      : direction === 'INBOUND'
      ? theme.colors.porcelain
      : theme.colors.topaz20};
  border-radius: ${({ direction, registrationType, forceMessageCaret }) =>
    !forceMessageCaret && registrationType === 'MANUAL'
      ? '6px'
      : direction === 'INBOUND'
      ? '8px 8px 8px 0'
      : '8px 8px 0 8px'};
  display: flex;

  & > * {
    border-radius: inherit;
    padding: 15px 15px 10px;
  }

  ${({ direction }) =>
    direction === 'INBOUND' ? 'margin-right: auto;' : 'margin-left: auto;'};

  ${up('md')} {
    background-color: ${({ direction, backgroundColor, theme }) =>
      backgroundColor
        ? theme.colors[backgroundColor]
        : direction === 'INBOUND'
        ? theme.colors.white
        : theme.colors.topaz20};
  }

  &.ci-highlighted {
    animation: ${ciHighlightedAnimation} 1.4s 4;
  }

  &.note-highlighted {
    animation: ${noteHighlightedAnimation} 1.4s 4;
  }
`;

const CenteredContainer = styled(FlexRow)`
  align-items: center;
`;

const DetailsMobileTrigger = styled.span`
  ${fadeInAnimation};
  font-family: ${({ theme }) => theme.font.medium};
  color: ${({ theme }) => theme.colors.flint};
  font-size: 10px;
  margin-left: 5px;
  line-height: 12px;
  position: relative;
  top: 1px;

  .separator {
    margin-right: 5px;
    position: relative;
    top: -1px;
  }

  ${up('md')} {
    display: none;
  }
`;

const StatusIcon = styled(Icon)`
  margin-left: 5px;

  svg {
    max-height: 20px;
    max-width: 20px;
  }
`;

export const DeliveryStatusIcon = ({
  messageStatus,
}: {
  messageStatus: LeadHistoryLogStatus;
}) => {
  const iconName = match(messageStatus)
    .with('DELIVERED', () => 'messageDeliveredStatus')
    .with('SENT', () => 'messageSentStatus')
    .with('READ', () => 'messageReadStatus')
    .with('FAILED', () => null)
    .otherwise(() => 'messagePendingStatus');

  if (!iconName) return null;

  return <StatusIcon type={iconName} lggTestId="delivery-status-icon" />;
};

export type InternalNoteType = 'CONVERSATION_NOTE' | 'CONTACT_NOTE';

type MessageBubbleWithDirectionProps = {
  testId: string;
  id?: string;
  direction: ContactInteractionDirection;
  registrationType: RegistrationType;
  isCall?: boolean;
  children: any;
  createdAt: string;
  details: ContactInteractionDetailsPopoverData | null;
  backgroundColor?: ColorPaletteItem;
  forceMessageCaret?: boolean;
  noteType?: InternalNoteType;
  contactInteractionType?: string;
  contactInteractionLogId?: string;
  deliveryStatus?: LeadHistoryLogStatus;
  $variant?: MessageBubbleWithDirectionContainerVariant;
  caretVariantOverride?: ContactInteractionDetailsCaretVariant;
  canEditInteraction?: boolean;
};

export const MessageBubbleWithDirection = ({
  direction,
  registrationType,
  children,
  createdAt,
  details,
  backgroundColor,
  forceMessageCaret = false,
  testId,
  id = '',
  isCall = false,
  noteType,
  contactInteractionType,
  contactInteractionLogId,
  deliveryStatus,
  $variant = MessageBubbleWithDirectionContainerVariant.REGULAR,
  caretVariantOverride,
  ...rest
}: MessageBubbleWithDirectionProps) => {
  const { visible, setVisible } = useVisible();
  const { t } = useTranslation(['common']);
  const { visible: showMobileDetailsTrigger, setVisible: setShowMobileDetailsTrigger } =
    useVisible();

  const { containerRef } = useOutstandingItem({ visible });

  return (
    <MessageBubbleWithDirectionContainer
      data-lgg-id={testId}
      ref={containerRef}
      data-id={id || details?.interactionId}
      registrationType={registrationType}
      direction={direction}
      backgroundColor={backgroundColor}
      forceMessageCaret={forceMessageCaret}
      $variant={$variant}
      {...rest}
    >
      <ContactInteractionDetails
        registrationType={registrationType}
        direction={direction}
        details={details}
        visible={visible}
        noteType={noteType}
        contactInteractionLogId={contactInteractionLogId}
        setVisibility={setVisible}
        isCall={isCall}
        contactInteractionType={contactInteractionType}
        caretVariantOverride={caretVariantOverride}
        onClick={() => {
          setShowMobileDetailsTrigger(!showMobileDetailsTrigger);
        }}
      >
        <div>
          {children}
          <CenteredContainer>
            <InteractionTime createdAt={createdAt} />
            {deliveryStatus ? (
              <DeliveryStatusIcon messageStatus={deliveryStatus} />
            ) : null}
            {showMobileDetailsTrigger && (
              <DetailsMobileTrigger
                onClick={(e) => {
                  e.stopPropagation();
                  setVisible(true);
                }}
              >
                <span className="separator">—</span>
                <span>{t('common:seeDetails')}</span>
              </DetailsMobileTrigger>
            )}
          </CenteredContainer>
        </div>
      </ContactInteractionDetails>
    </MessageBubbleWithDirectionContainer>
  );
};

const MessageTitle = styled.span`
  color: rgba(87, 114, 138, 0.75);
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  letter-spacing: normal;
  line-height: 15px;
  text-align: left;

  strong {
    color: ${({ theme }) => theme.colors.smalt};
    font-weight: unset;
  }
`;

const UniformTitle = styled(MessageTitle)`
  color: ${({ theme }) => theme.colors.smalt};
`;

const StyledIcon = styled(Icon)`
  margin-right: 10px;
`;

type BubbleMessageHeaderProps = {
  icon?: string;
  title: string | ReactElement;
  contactInteraction: ContactInteractionUnion;
};

export const BubbleMessageHeader = memo<BubbleMessageHeaderProps>(
  ({ title, icon, contactInteraction, ...props }) => {
    const TitleComponent = typeof title === 'string' ? UniformTitle : MessageTitle;

    return (
      <FlexRow {...props}>
        {icon && (
          <StyledIcon
            className="interaction-icon"
            type={icon}
            lggTestId={`contact-interaction-icon-${icon}`}
          />
        )}
        <TitleComponent data-lgg-id="contact-interaction-card-title">
          {title}
          <ManualRegistrationIcon contactInteraction={contactInteraction} />
        </TitleComponent>
      </FlexRow>
    );
  },
);

const AutomatedMessageHeaderIcon = styled(Icon)`
  margin-right: 6px;
`;

const AutomatedMessageHeaderTitle = styled.p`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  line-height: 16px;
  margin: 0;
`;

type AutomatedMessageBubbleHeaderProps = {
  icon?: string;
  title: string;
};

export const AutomatedMessageBubbleHeader = memo<AutomatedMessageBubbleHeaderProps>(
  ({ title, icon, ...props }) => {
    return (
      <FlexRow {...props}>
        {icon && (
          <AutomatedMessageHeaderIcon
            type={icon}
            lggTestId={`automated-contact-interaction-icon-${icon}`}
          />
        )}
        <AutomatedMessageHeaderTitle data-lgg-id="automated-contact-interaction-card-title">
          {title}
        </AutomatedMessageHeaderTitle>
      </FlexRow>
    );
  },
);

const StyledCenteredContainer = styled(CenteredContainer)`
  margin-left: auto;
  margin-top: 8px;
  width: max-content;
  color: ${({ theme }) => theme.colors.flint};
  font-size: 10px;
  letter-spacing: -0.2px;

  span {
    margin-left: 5px;
  }
`;

type FailedMessageIndicatorProps = Partial<
  Pick<ContactInteractionComponent, 'statusDetails'>
>;

export const FailedMessageIndicator = memo<FailedMessageIndicatorProps>(
  ({ statusDetails }) => {
    const { t } = useTranslation(['conversations']);
    const error = statusDetails?.message;
    const errorComponent = useMemo(
      () => (
        <StyledCenteredContainer>
          <Icon type="infoAlertRedIcon" lggTestId="failed-interaction-status-message" />
          <span>
            {t('conversations:contactInteractionBubble.couldntSendInteraction')}
          </span>
        </StyledCenteredContainer>
      ),
      [t],
    );

    if (error) {
      return (
        <Tooltip title={error} trigger={['hover']}>
          {errorComponent}
        </Tooltip>
      );
    }

    return errorComponent;
  },
);

export const RepliedMessageRenderer = (props: {
  contactInteraction: ContactInteractionUnion;
}) => {
  const { contactInteraction, ...rest } = props;

  const relatedContactInteraction = match(contactInteraction)
    .with(
      {
        message: {
          relatedContactInteraction: P.nonNullable,
        },
      },
      (contactInteraction) => contactInteraction.message.relatedContactInteraction,
    )
    .with(
      {
        attachments: [
          {
            __typename: P.union(
              'ContactInteractionAttachmentFile',
              'ContactInteractionAttachmentLocation',
              'ContactInteractionAttachmentContacts',
            ),
            relatedContactInteraction: P.nonNullable,
          },
        ],
      },
      (contactInteraction) => {
        return 'attachments' in contactInteraction
          ? (contactInteraction.attachments[0]
              .relatedContactInteraction as ContactInteractionUnion)
          : null;
      },
    )
    .otherwise(() => null);

  if (!relatedContactInteraction) {
    return null;
  }

  return <RepliedItemPreview {...rest} contactInteraction={relatedContactInteraction} />;
};

const StyledContent = styled.div`
  cursor: not-allowed;
  filter: blur(4.5px);
`;

export const Content = memo<PropsWithChildren<{ isBlurred?: boolean }>>(
  ({ children, isBlurred }) => {
    const { t } = useTranslation(['conversations']);
    const breakpointUpMd = useBreakpoint(up('md'));
    const blurredContent = useMemo(() => {
      return <StyledContent>{children}</StyledContent>;
    }, [children]);

    if (!isBlurred) {
      return <>{children}</>;
    }

    if (!breakpointUpMd) {
      return blurredContent;
    }

    return (
      <Tooltip
        trigger={['hover']}
        title={t('conversations:maskedContactInteractionContent')}
      >
        {blurredContent}
      </Tooltip>
    );
  },
);

export const ItemMessage = styled(InteractionTextContent)`
  margin-bottom: 5px;
`;

export const ItemCaption = styled(ItemMessage)`
  margin-top: 10px;
  margin-left: 5px;
`;

const ExternalItemContainer = styled.div`
  width: 100%;

  ${up('md')} {
    max-width: 290px;
  }
`;

const ExternalItemDisclaimerText = styled.div`
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.regular};
  padding: 0 15px 13px;
  margin: 0 -15px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.flint20};
`;

const externalItemBaseStyles = css`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.storm};
  display: flex;
  flex-direction: column;
  font-family: ${({ theme }) => theme.font.medium};
  gap: 10px;
  margin: 10px 0px 5px 0px;
  padding: 10px 15px;

  ${up('md')} {
    background: ${({ theme }) => theme.colors.porcelain};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.storm};
  }
`;

const ExternalItemContent = styled.div`
  ${externalItemBaseStyles};
`;

const ReferralLinkContainer = styled.a`
  ${externalItemBaseStyles};
`;

const StoryItemContent = styled.div`
  ${externalItemBaseStyles};

  padding: 10px;
`;

const RefIcon = styled(Icon)`
  margin-right: 10px;

  & svg {
    height: 14px;
    width: 14px;
  }
`;

const DropdownIcon = styled(Icon)`
  & svg {
    height: 12px;
    width: 12px;
  }
`;

const ThumbnailWrapper = styled.div`
  max-width: 270px;
`;

export const AssetWrapper = styled(Center)`
  border-radius: 6px 6px 6px 0;
  height: max-content;
  max-height: 325px;
  min-height: 50px;
  min-width: 60px;
  overflow: hidden;
  position: relative;
  width: 100%;

  img {
    max-width: 260px;
    object-fit: cover;
    width: 100%;
  }
`;

const StoryWrapper = styled(Center)`
  border-radius: 6px;
  cursor: pointer;
  height: 230px;
  overflow: hidden;
  position: relative;
  width: 176px;

  ${up('md')} {
    height: 297px;
    width: 196px;
  }

  img {
    object-fit: cover;
    width: 100%;
  }
`;

const BodyContent = styled.p`
  margin: -6px 0 0;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  font-stretch: normal;
  line-height: 1.33;
  letter-spacing: normal;
  max-height: 32px;
  overflow: hidden;
  position: relative;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const HeadlineContainer = styled(FlexRow)`
  justify-content: space-between;
  align-items: center;
`;

export const ContentContainer = styled(FlexRow)`
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.regular};
  align-items: center;
`;

const Headline = styled.span<{ hasHeadline: boolean }>`
  color: ${({ theme, hasHeadline }) =>
    hasHeadline ? theme.colors.smalt : theme.colors.gogo};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: ${({ hasHeadline }) => `${hasHeadline ? 13 : 12}px`};
  font-weight: 500;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
`;

const StoryVideo = styled.video`
  height: 100%;
`;

export const ReferralItemRenderer = ({
  contactInteraction,
}: {
  contactInteraction: ContactInteractionUnion;
}) => {
  const { t } = useTranslation(['conversations']);

  const referral = match(contactInteraction)
    .with(
      {
        message: {
          referral: P.not(P.nullish),
        },
      },
      (contactInteraction) => contactInteraction.message.referral,
    )
    .otherwise(() => null);

  if (!referral) {
    return null;
  }

  const renderImageOrThumbnail = () =>
    match(referral)
      .with(
        { __typename: 'ContactInteractionReferralImage', imageUrl: P.string },
        (referral) => {
          return (
            <AssetWrapper>
              <img
                src={referral.imageUrl}
                alt={referral?.headline || 'Referral image'}
                data-lgg-id="referral-image"
              />
            </AssetWrapper>
          );
        },
      )
      .with(
        { __typename: 'ContactInteractionReferralVideo', thumbnailUrl: P.string },
        (referral) => {
          return (
            <ThumbnailWrapper>
              <AssetWrapper>
                <PlayIcon type="play" />
                <img
                  src={referral.thumbnailUrl}
                  alt={referral?.headline || 'Referral thumbnail'}
                  data-lgg-id="referral-thumbnail"
                />
                <VideoOverlay />
              </AssetWrapper>
            </ThumbnailWrapper>
          );
        },
      )
      .otherwise(() => null);

  const iconName = match(referral.source)
    .with(P.union('instagram', 'facebook'), (source) => source)
    .with('ad', () => null)
    .otherwise(() => 'externalLink');

  const sourceName = match(referral)
    .with({ source: 'instagram' }, () => 'Instagram')
    .with({ source: 'facebook' }, () => 'Facebook')
    .with({ source: 'ad' }, () => t('conversations:referralPreview.source.ad'))
    .otherwise(() => null);

  const referralPreviewContent = (
    <>
      {renderImageOrThumbnail()}
      <HeadlineContainer>
        <ContentContainer>
          {iconName && !referral.body && (
            <RefIcon
              type={iconName}
              data-lgg-id={`contact-interaction-icon-${iconName}`}
            />
          )}
          <Headline hasHeadline={Boolean(referral?.headline)}>
            {referral?.headline ?? t('conversations:referralPreview.link')}
          </Headline>
        </ContentContainer>
        {referral.type === 'link' && referral.source !== 'ad' && !referral.body && (
          <DropdownIcon type="arrowRightWide" />
        )}
      </HeadlineContainer>
      {referral.body && <BodyContent>{referral.body}</BodyContent>}
    </>
  );

  return (
    <ExternalItemContainer>
      <ExternalItemDisclaimerText data-lgg-id="referral-text">
        {t('conversations:referralPreview.receivedViaSource', {
          source: sourceName || t('conversations:referralPreview.source.externalSite'),
        })}
      </ExternalItemDisclaimerText>
      {referral.url ? (
        <ReferralLinkContainer
          href={referral.url}
          target="_blank"
          referrerPolicy="no-referrer"
          rel="noreferrer"
        >
          {referralPreviewContent}
        </ReferralLinkContainer>
      ) : (
        <ExternalItemContent>{referralPreviewContent}</ExternalItemContent>
      )}
    </ExternalItemContainer>
  );
};

export const RepliedStoryRenderer = ({
  contactInteraction,
}: {
  contactInteraction: ContactInteractionUnion;
}) => {
  const { t } = useTranslation(['conversations']);
  const assetPreviewModalVisibilityHandler = useVisible();

  const repliedStory = match(contactInteraction)
    .with(
      {
        message: {
          repliedStory: P.not(P.nullish),
        },
      },
      (contactInteraction) => contactInteraction.message.repliedStory,
    )
    .otherwise(() => null);

  if (!repliedStory) {
    return null;
  }

  const storyPreview = match(repliedStory)
    .with({ type: 'IMAGE' }, ({ url }) => (
      <>
        <StoryWrapper onClick={assetPreviewModalVisibilityHandler.show}>
          <img src={url} alt="Replied story" data-lgg-id="replied-story-image" />
        </StoryWrapper>
        <AttachmentPreviewModal
          visible={assetPreviewModalVisibilityHandler.visible}
          onClose={assetPreviewModalVisibilityHandler.close}
          type="IMAGE"
          url={url}
        />
      </>
    ))
    .with({ type: 'VIDEO' }, ({ url }) => (
      <>
        <StoryWrapper onClick={assetPreviewModalVisibilityHandler.show}>
          <PlayIcon type="play" />
          <StoryVideo data-lgg-id="replied-story-video">
            {/* Setting video time to #t=0.001 allows video preview to be shown on safari mobile */}
            {/* https://developer.apple.com/forums/thread/129377?answerId=684485022#684485022 */}
            <source src={`${url}#t=0.001`} data-lgg-id="replied-story-video-source" />
          </StoryVideo>
          <VideoOverlay />
        </StoryWrapper>
        <AttachmentPreviewModal
          visible={assetPreviewModalVisibilityHandler.visible}
          onClose={assetPreviewModalVisibilityHandler.close}
          type="VIDEO"
          url={url}
        />
      </>
    ))
    .otherwise(() => null);

  return (
    <ExternalItemContainer>
      <ExternalItemDisclaimerText data-lgg-id="replied-story-title">
        {t('conversations:repliedStory.title')}
      </ExternalItemDisclaimerText>
      <StoryItemContent>{storyPreview}</StoryItemContent>
    </ExternalItemContainer>
  );
};

const StyledReadMoreLabel = styled.span`
  color: ${({ theme }) => theme.colors.gogo};
  cursor: pointer;
  display: block;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  font-style: normal;
  line-height: 15px;
  margin-top: 5px;
  margin-bottom: 5px;
`;

export const ReadMoreLabel = (props: { onClick: VoidFunction }) => {
  const { t } = useTranslation(['common']);

  return (
    <StyledReadMoreLabel
      data-lgg-id="contact-interaction-read-more"
      onClick={props.onClick}
    >
      {t('common:readMore')}
    </StyledReadMoreLabel>
  );
};

const extractText = (children: ReactNode): string => {
  let text = '';

  React.Children.forEach(children, (child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      text += child.toString();
    } else if (React.isValidElement(child) && child.props.children) {
      text += extractText(child.props.children);
    }
  });

  return text;
};

const TruncatedTextContainer = styled.div<{ $maxLines: number | null }>`
  ${({ $maxLines }) =>
    $maxLines &&
    `
      display: -webkit-box;
      -webkit-line-clamp: ${$maxLines};
      -webkit-box-orient: vertical;
      overflow: hidden;
  `}
`;

export const TruncatedText = memo((props) => {
  const [showingMore, setShowingMore] = useState(false);
  const breakpointUpMd = useBreakpoint(up('md'));
  const maxCharacters = breakpointUpMd ? 2000 : 700;
  const text = useMemo(() => extractText(props.children), [props.children]);
  const isTextLongEnough = (text?.length ?? 0) > maxCharacters;
  const displayShowMore = isTextLongEnough && !showingMore;
  const maxLines = displayShowMore ? (breakpointUpMd ? 24 : 20) : null;

  return (
    <FlexColumn>
      <TruncatedTextContainer
        data-lgg-id="truncated-container"
        className={c({
          truncated: maxLines !== null,
        })}
        $maxLines={maxLines}
      >
        {props.children}
      </TruncatedTextContainer>
      {displayShowMore && <ReadMoreLabel onClick={() => setShowingMore(true)} />}
    </FlexColumn>
  );
});

const FlowResponseContent = styled.div`
  color: ${({ theme }) => theme.colors.steel};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 16px;
  margin: 0;

  ${up('md')} {
    font-size: 13px;
  }
`;
const FlowResponseContainer = styled.pre`
  background: ${({ theme }) => theme.colors.alabaster};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.koala};
  color: ${({ theme }) => theme.colors.steel};
  font-family: ${({ theme }) => theme.font.dmMonoMedium};
  font-size: 13px;
  font-style: normal;
  line-height: 16px;
  padding: 10px;
`;

type FlowResponseProps = {
  responses: string;
};

export const FlowResponse = ({ responses }: FlowResponseProps) => {
  const { t } = useTranslation(['conversations']);

  return (
    <FlowResponseContent data-lgg-id="whatsapp-flow-response-content">
      {t('conversations:contactInteractionBubble.whatsapp.flowResponse')}
      <FlowResponseContainer data-lgg-id="whatsapp-flow-response">
        {JSON.stringify(JSON.parse(responses), null, 2)}
      </FlowResponseContainer>
    </FlowResponseContent>
  );
};
